import type {
  AdminAssignment,
  AdminUserSummary,
  ContextShape,
  Dimension,
  EffectiveAccess,
} from './types';

/**
 * What the administration screens offer, from the caller's ACTIVE effective
 * access (its administration permission keys, context type, own_ids) and the
 * platform-owner flag (GET me/effective-access answers access administrators
 * only; anyone else gets a refusal and sees nothing to administer). Every rule
 * here mirrors a server rule in pnet-identity-node access-admin.policy.ts
 * (permissionDeny, maySelfAdminister, targetDeny, statusDeny, emailDeny,
 * mfaResetDeny, assignmentDeny); the server re-checks everything, these only
 * decide what is shown. Nothing here is a client-only rule.
 */

export const PERMISSION_USER_LIST = 'user_list';
export const PERMISSION_USER_CREATE = 'user_create';
export const PERMISSION_USER_EDIT = 'user_edit';
export const PERMISSION_USER_STATUS = 'user_status';
export const PERMISSION_USER_EDIT_SELF = 'user_edit_self';
export const PERMISSION_SECURITY_RULES_MANAGE = 'security_rules_manage';
export const PERMISSION_ROLE_PREVIEW = 'role_preview';

/**
 * The access administration keys (pnet-identity-node access/user-facing.ts
 * ACCESS_ADMIN_PERMISSIONS): GET me/effective-access answers a caller whose
 * ACTIVE role holds one of them, or a platform owner; everyone else gets 403
 * permission_required. A change on either side must change both.
 */
export const ACCESS_ADMIN_PERMISSIONS: readonly string[] = [
  PERMISSION_USER_LIST,
  PERMISSION_USER_CREATE,
  PERMISSION_USER_EDIT,
  PERMISSION_USER_STATUS,
  'role_list',
  'role_create',
  'role_edit',
  'role_delete',
  'permission_create',
  'tiles_create',
  PERMISSION_SECURITY_RULES_MANAGE,
  PERMISSION_ROLE_PREVIEW,
];

/**
 * May the UI read GET me/effective-access without a refusal? Only when the
 * active role holds an access administration key (`has`: the active role's
 * permission check). A platform owner whose active role holds none is not
 * asked: the UI cannot know the flag without the call, so the
 * platform-owner-only links stay hidden in that role.
 */
export const mayReadEffectiveAccess = (has: (key: string) => boolean): boolean =>
  ACCESS_ADMIN_PERMISSIONS.some((key) => has(key));

export interface Capabilities {
  /** the caller's users.id (effective-access user_id); null when unknown */
  userId: number | null;
  /** the caller's active context (tenant admins work only in it) */
  context: ContextShape | null;
  isInternal: boolean;
  /** users.is_admin = 1 until §8 (the server's platformOwner), from effective-access */
  isPlatformOwner: boolean;
  /**
   * Why a tenant admin cannot add users / roles right now, shown up front
   * (the server answers users_self_service_disabled / seat_limit_reached).
   * False when unknown; always false for internal admins.
   */
  selfServiceOff: boolean;
  /**
   * PartnershipNet manages the tenant's access (effective-access tenant_settings.manage_users
   * false, owner 2026-10-19): the server refuses every user change of a tenant admin
   * (organisation_managed_by_asi), so the account actions are not offered. Always false for
   * internal admins and on servers that do not send the flag.
   */
  usersReadOnly: boolean;
  seatLimitReached: boolean;
  seatLimit: number | null;
  seatsUsed: number | null;
  canList: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canStatus: boolean;
  /** user_edit_self, effective only together with user_edit */
  canEditSelf: boolean;
  /**
   * Settings > Manage Users > Rules (§13.16): an INTERNAL active role with
   * security_rules_manage (the Application Admin role); being a platform owner
   * does not count (owner decision 2026-10-07). Run now and the access
   * reviews need the same.
   */
  canManageSecurityRules: boolean;
  /**
   * Settings > Manage Users > Access review (owner 2026-10-07): a security
   * rules manager (every organisation) or an organisation admin (user_edit in
   * the active customer role; their own organisation only).
   */
  canReviewAccess: boolean;
  /**
   * "Preview as this role" (Authorization-Plan §13.7): an INTERNAL active role
   * with role_preview (the Application Admin role); false while previewing
   * (the effective access is then the previewed role's).
   */
  canPreview: boolean;
  /** the caller's own active assignment (role_assignment_id), null when unknown */
  activeAssignmentId: number | null;
  /** dimensions the caller is limited on (tenant admins: their tenant dimension) */
  limitedDimensions: Dimension[];
  /** per dimension: may the caller grant ALL */
  allAllowed: Record<Dimension, boolean>;
  /** per dimension: the only ids the caller may grant; null = any */
  allowedIds: Record<Dimension, number[] | null>;
}

const DIMS: Dimension[] = ['brand', 'partner', 'partner_category', 'partner_type', 'country'];

/**
 * Scope ceiling (server, owner change 2026-10-05): tenant admins are limited
 * only on their TENANT dimension - a brand admin on brand (own brand), a right
 * holder admin on partner (own partner), an agency admin on brand and partner
 * (own); partner category, partner type and country are free. Internal admins
 * are not limited.
 */
export const TENANT_DIMENSIONS: Record<string, Dimension[]> = {
  INTERNAL: [],
  BRAND: ['brand'],
  RIGHT_HOLDER: ['partner'],
  AGENCY: ['brand', 'partner'],
};

/**
 * The ids a tenant admin may grant on a limited dimension: the ids of their
 * own active scope (`own_ids`: union over their rules, children expanded, as
 * the server computes it); when their scope names none, the tenant's own
 * brand / partner.
 */
function ownIds(effective: EffectiveAccess, d: Dimension): number[] {
  const ids = new Set<number>(
    (effective.own_ids?.[d] ?? []).map(Number).filter((id) => Number.isInteger(id) && id > 0),
  );
  if (ids.size === 0) {
    const ctx = effective.context;
    if (d === 'brand' && ctx?.brand_id) ids.add(ctx.brand_id);
    if (d === 'partner' && ctx?.property_id) ids.add(ctx.property_id);
  }
  return [...ids].sort((a, b) => a - b);
}

/**
 * `tenant_settings` of /me/effective-access (fields guarded: older servers
 * answer without them). Self-service off only when the switch is explicitly
 * false; the seat limit counts as reached only when both the limit and the
 * seats in use are known.
 */
function tenantState(effective: EffectiveAccess | null, isInternal: boolean) {
  const t = isInternal ? null : (effective?.tenant_settings ?? null);
  const seatLimit = typeof t?.seat_limit === 'number' ? t.seat_limit : null;
  const usedRaw = t?.seats_used ?? t?.seats_occupied;
  const seatsUsed = typeof usedRaw === 'number' ? usedRaw : null;
  return {
    selfServiceOff: t?.users_self_service === false,
    usersReadOnly: t?.manage_users === false,
    seatLimit,
    seatsUsed,
    seatLimitReached: seatLimit !== null && seatsUsed !== null && seatsUsed >= seatLimit,
  };
}

export function capabilitiesFrom(
  effective: EffectiveAccess | null,
  /** override for tests; default: effective-access `is_platform_owner` (false when absent) */
  isPlatformOwner: boolean = effective?.is_platform_owner === true,
): Capabilities {
  const perms = new Set(effective?.permissions ?? []);
  const type = String(effective?.context?.type ?? '');
  const isInternal = type === 'INTERNAL';
  const limitedDimensions = effective ? (TENANT_DIMENSIONS[type] ?? []) : [];
  const allAllowed = {} as Record<Dimension, boolean>;
  const allowedIds = {} as Record<Dimension, number[] | null>;
  for (const d of DIMS) {
    const limited = limitedDimensions.includes(d);
    allAllowed[d] = !limited;
    allowedIds[d] = limited && effective ? ownIds(effective, d) : null;
  }
  const tenant = tenantState(effective, isInternal);
  // users_self_service off: the server refuses every action of a tenant admin
  // (permissionDeny -> users_self_service_disabled), so nothing is offered; nor when
  // PartnershipNet manages the tenant's access (managedByDeny -> organisation_managed_by_asi).
  const on = !tenant.selfServiceOff && !tenant.usersReadOnly;
  const userId = Number(effective?.user_id);
  const activeAssignmentId = Number(effective?.role_assignment_id);
  return {
    userId: Number.isInteger(userId) && userId > 0 ? userId : null,
    context: effective?.context ?? null,
    isInternal,
    isPlatformOwner,
    ...tenant,
    canList: perms.has(PERMISSION_USER_LIST),
    canCreate: on && perms.has(PERMISSION_USER_CREATE),
    canEdit: on && perms.has(PERMISSION_USER_EDIT),
    canStatus: on && perms.has(PERMISSION_USER_STATUS),
    canEditSelf: on && perms.has(PERMISSION_USER_EDIT_SELF) && perms.has(PERMISSION_USER_EDIT),
    canReviewAccess:
      (isInternal && perms.has(PERMISSION_SECURITY_RULES_MANAGE)) ||
      (['BRAND', 'RIGHT_HOLDER', 'AGENCY'].includes(type) && perms.has(PERMISSION_USER_EDIT)),
    canManageSecurityRules: isInternal && perms.has(PERMISSION_SECURITY_RULES_MANAGE),
    canPreview: isInternal && perms.has(PERMISSION_ROLE_PREVIEW),
    activeAssignmentId:
      Number.isInteger(activeAssignmentId) && activeAssignmentId > 0 ? activeAssignmentId : null,
    limitedDimensions,
    allAllowed,
    allowedIds,
  };
}

/**
 * "Preview as this role" on an assignment row (§13.7): the caller may
 * preview, the assignment is active, it is not the caller's own active
 * assignment (that is what they see already) and its role is not a system
 * role (roles.system_key, e.g. Application Admin).
 */
export const mayPreviewAssignment = (
  caps: Pick<Capabilities, 'canPreview' | 'activeAssignmentId'>,
  a: Pick<AdminAssignment, 'id' | 'status' | 'role'>,
): boolean =>
  caps.canPreview &&
  a.status === 1 &&
  a.id !== caps.activeAssignmentId &&
  !(typeof a.role?.system_key === 'string' && a.role.system_key.trim() !== '');

type Target = Pick<AdminUserSummary, 'is_self' | 'is_platform_owner'>;

/** targetDeny (write): never the own record without user_edit_self; tenant admins never a platform owner. */
export const mayManageTarget = (caps: Capabilities, target: Target): boolean =>
  (!target.is_self || caps.canEditSelf) && (caps.isInternal || !target.is_platform_owner);

/** Profile fields and assignments of `target` (user_edit). */
export const mayEditUser = (caps: Capabilities, target: Target): boolean =>
  caps.canEdit && mayManageTarget(caps, target);

/** statusDeny: never self; a platform owner only by a platform owner (user_status). */
export const mayChangeStatus = (caps: Capabilities, target: Target): boolean =>
  caps.canStatus &&
  !target.is_self &&
  (!target.is_platform_owner || caps.isPlatformOwner) &&
  mayManageTarget(caps, target);

/** mfaResetDeny: never self; a platform owner only by a platform owner (user_edit). */
export const mayResetMfa = (caps: Capabilities, target: Target): boolean =>
  caps.canEdit &&
  !target.is_self &&
  (!target.is_platform_owner || caps.isPlatformOwner) &&
  mayManageTarget(caps, target);

/**
 * "Correct home organisation" (PUT admin/users/{id}/home, owner 2026-10-31): Super Admins
 * only (user_edit), never their own record. It replaces Change type and Change organisation
 * (both answer 410 use_correct_home); the server refuses it while the person holds a running
 * role anywhere (409 user_has_running_roles).
 */
export const mayCorrectHome = (caps: Capabilities, target: Target): boolean =>
  caps.canEdit && caps.isPlatformOwner && !target.is_self && mayManageTarget(caps, target);

/** The client organisation types: their roles change only through the organisation's process. */
export const CLIENT_CONTEXT_TYPES: readonly string[] = ['BRAND', 'RIGHT_HOLDER', 'AGENCY'];

/**
 * Is a role row in a client organisation (test organisations included)? Its role changes
 * only through the membership actions (Renew, Change, End access, Confirm review) and
 * invitations; the old assignment routes answer 403 use_membership_routes for it (owner
 * 2026-10-31). The Internal organisation keeps + Role, Edit, Disable and Enable.
 */
export const isClientRow = (a: Pick<AdminAssignment, 'context'>): boolean =>
  CLIENT_CONTEXT_TYPES.includes(String(a.context.type));

/** 'Y-m-d H:i:s' in UTC, the database clock (valid_from / valid_until). */
const utcNow = (): string => new Date().toISOString().slice(0, 19).replace('T', ' ');

/**
 * Does a role row still run: enabled, and valid now or starting later (not expired)? The
 * server refuses "Correct home organisation" while any such row exists (409
 * user_has_running_roles); an enabled row that already expired does not block (the server
 * closes it in the same step).
 */
export const isRunningAssignment = (
  a: Pick<AdminAssignment, 'status' | 'valid_until'>,
  now: string = utcNow(),
): boolean => a.status === 1 && (!a.valid_until || a.valid_until > now);

/**
 * Is an assignment in the customer user's one organisation (server
 * assignmentOrganisationError)? Internal users: always; a customer user
 * without an organisation yet: never (nothing new until a Super Admin
 * corrects the home organisation).
 */
export const fitsOrganisation = (
  userType: string | null,
  organisation: { context_id: number } | null | undefined,
  contextId: number,
): boolean =>
  !(userType === 'BRAND' || userType === 'RIGHT_HOLDER' || userType === 'AGENCY') ||
  (organisation != null && organisation.context_id === contextId);

/**
 * A guest row by the server's own judgement, for when the row carries no membership_type
 * (the memberships list is not loaded yet or not readable): a customer user's role in another
 * client organisation that the server does not flag outside_organisation. Only a guest
 * (external) row there is let through (access.compiler isOutsideOrganisation).
 */
export const isGuestByServer = (
  userType: string | null,
  organisation: { context_id: number } | null | undefined,
  a: Pick<AdminAssignment, 'context' | 'outside_organisation'>,
): boolean =>
  (userType === 'BRAND' || userType === 'RIGHT_HOLDER' || userType === 'AGENCY') &&
  isClientRow(a) &&
  a.outside_organisation === false &&
  organisation?.context_id !== a.context.id;

/**
 * Member or guest (external) for a role row of the person's page. The row's own
 * membership_type (the person record marks every row) is the answer; an older server
 * without it: the person's membership of that row (the memberships list), then the
 * server's own judgement of the row (isGuestByServer, outside_organisation). Null: not known.
 */
export const membershipTypeOf = (
  userType: string | null,
  organisation: { context_id: number } | null | undefined,
  a: Pick<AdminAssignment, 'context' | 'outside_organisation' | 'membership_type'>,
  listed?: 'member' | 'external' | null,
): 'member' | 'external' | null =>
  a.membership_type ?? listed ?? (isGuestByServer(userType, organisation, a) ? 'external' : null);

/**
 * Does an assignment's organisation fit the person's type (server
 * assignmentTypeError)? Internal users: any; customer users: their own type
 * only; no type yet (old data): nothing new until a Super Admin corrects the
 * home organisation.
 */
export const fitsUserType = (userType: string | null, contextType: string): boolean =>
  userType === 'INTERNAL' || (userType !== null && userType === contextType);

/** emailDeny: platform owners only, never their own. */
export const mayChangeEmail = (caps: Capabilities, target: Target): boolean =>
  caps.canEdit && caps.isPlatformOwner && !target.is_self && mayManageTarget(caps, target);

/** Disable / re-enable an assignment: a status change, never on one's own (self_status_change). */
export const mayChangeAssignmentStatus = (caps: Capabilities, target: Target): boolean =>
  mayEditUser(caps, target) && !target.is_self;

/** Validity of an EXISTING assignment of one's own cannot change (self_validity_change). */
export const mayChangeValidity = (target: Target, existing: boolean): boolean =>
  !(target.is_self && existing);

/** The Internal organisation: platform owners only (internal_context_requires_platform_owner). */
export const mayUseInternalContext = (caps: Capabilities): boolean =>
  caps.isPlatformOwner && caps.isInternal;

/**
 * Add user > "ASI staff" (POST admin/users creates Internal accounts only, owner
 * 2026-10-31): user_create, and the first role is in the Internal organisation, which only
 * a Super Admin in an Internal role can give (internal_context_requires_platform_owner).
 */
export const mayAddStaff = (caps: Capabilities): boolean =>
  caps.canCreate && mayUseInternalContext(caps);

/** Internal assignments only for company addresses (server: 422 internal_requires_company_domain). */
export const INTERNAL_EMAIL_DOMAINS = ['partnership-net.com', 'advanced-sponsorship-insights.com'];

export const isCompanyEmail = (email: string | null | undefined): boolean => {
  const domain =
    String(email ?? '')
      .trim()
      .toLowerCase()
      .split('@')[1] ?? '';
  return INTERNAL_EMAIL_DOMAINS.includes(domain);
};

/** Internal admins pick any tenant; tenant admins work in their own (context_outside_tenant). */
export const mayPickTenant = (caps: Capabilities): boolean => caps.isInternal;
